import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { bsc } from 'wagmi/chains';
import { Toaster } from './components/ui/sonner';
import { config } from './config/wagmi';
import { Header } from './components/Header';
import { RewardsPage } from './pages/RewardsPage';
import { HomePage } from './pages/HomePage';
import { ClaimPage } from './pages/ClaimPage';
import { VerifyPage } from './pages/VerifyPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
// Legacy admin pages removed - now using SecretAdminPage with wallet auth
import { EmbedRewardsPage } from './pages/EmbedRewardsPage';
import { EmbedVerifyPage } from './pages/EmbedVerifyPage';
import { EmbedSwapPage } from './pages/EmbedSwapPage';
import { SecretAdminPage } from './pages/SecretAdminPage';
import TipQRPage from './pages/TipQRPage';
import RegisterQRPage from './pages/RegisterQRPage';
import MyQRPage from './pages/MyQRPage';
import DashboardPage from './pages/DashboardPage';
import TestPWAPage from './pages/TestPWAPage';
import DiscoverPage from './pages/DiscoverPage';
import { EmbedDiscoverPage } from './pages/EmbedDiscoverPage';
import '@rainbow-me/rainbowkit/styles.css';
import './App.css';

const queryClient = new QueryClient();

// Layout wrapper that conditionally shows header
const Layout = ({ children }) => {
  // No header on any page - pages are standalone
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--tm-bg-primary)', color: 'var(--tm-text-primary)' }}>
      {children}
    </div>
  );
};

function App() {
  // Part 1: Capture beforeinstallprompt for PWA install flow
  React.useEffect(() => {
    const handler = (e) => { e.preventDefault(); window.__pwaPrompt = e; window.dispatchEvent(new Event('pwa-prompt-ready')); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // PWA dark mode: tag body when running as installed app
  React.useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      document.body.classList.add('pwa-dark');
    }
  }, []);

  // Part 6: Visit counter for dashboard install card
  React.useEffect(() => {
    localStorage.setItem('visit_count', String((parseInt(localStorage.getItem('visit_count') || '0', 10)) + 1));
  }, []);

  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <RainbowKitProvider initialChain={bsc} locale="en-US">
          <BrowserRouter>
            <Layout>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/rewards" element={<RewardsPage />} />
                <Route path="/claim" element={<ClaimPage />} />
                <Route path="/verify" element={<VerifyPage />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                
                {/* QR Tipping route */}
                <Route path="/tip/:qrId" element={<TipQRPage />} />
                
                {/* QR Self-Registration route */}
                <Route path="/register-qr" element={<RegisterQRPage />} />
                
                {/* My QR Code page (view/download) */}
                <Route path="/my-qr/:qrId" element={<MyQRPage />} />
                
                {/* Creator Dashboard */}
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/test-pwa" element={<TestPWAPage />} />
                
                {/* Discover - Creator Directory */}
                <Route path="/discover" element={<DiscoverPage />} />
                
                {/* DEX Swap - full page */}
                <Route path="/swap" element={<EmbedSwapPage />} />
                
                {/* Embed routes (legacy, no header) */}
                <Route path="/embed/rewards" element={<EmbedRewardsPage />} />
                <Route path="/embed/verify" element={<EmbedVerifyPage />} />
                <Route path="/embed/swap" element={<EmbedSwapPage />} />
                <Route path="/embed/discover" element={<EmbedDiscoverPage />} />
                
                {/* Secret admin route - uses SecretAdminPage which has BOTH epoch AND YouTube admin features */}
                <Route path="/tipme-admin-x7k9m2" element={<SecretAdminPage />} />
              </Routes>
            </Layout>
            <Toaster />
          </BrowserRouter>
        </RainbowKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}

export default App;
